import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  wbTemplatesApi,
  type WbTemplateCatalogEntry,
  type WbInstantiateEvent,
  type WbBusinessInfo,
  type WbInstantiateBody,
} from '@/lib/api/services/websiteBuilder';
import {
  organizationApi,
  type OrganizationAiContextResponse,
} from '@/lib/api/services/organization';
import { useAuth } from '@/components/AuthProvider';
import { TemplateCard } from './templates/TemplateCard';
import { MiniForm } from './templates/MiniForm';
import { InstantiateProgress } from './templates/InstantiateProgress';

interface TemplateGalleryProps {
  addToSiteId?: string;
  onComplete: (siteId: string, firstPageId: string) => void;
  onClose?: () => void;
  onStartBlank?: () => void;
}

function isAiContextSufficient(
  ctx: OrganizationAiContextResponse | null,
): boolean {
  if (!ctx) return false;
  if (!ctx.hasContent) return false;
  const name = (ctx.parsed?.business_name ?? '').trim();
  return name.length > 0;
}

function deriveLockedFields(
  ctx: OrganizationAiContextResponse | null,
): Array<keyof WbBusinessInfo> {
  if (!ctx?.parsed) return [];
  const p = ctx.parsed;
  const locked: Array<keyof WbBusinessInfo> = [];
  if ((p.business_name ?? '').trim()) locked.push('business_name');
  if ((p.services ?? '').trim()) locked.push('services');
  if ((p.support_phone ?? '').trim()) locked.push('support_phone');
  if ((p.support_email ?? '').trim()) locked.push('support_email');
  if ((p.instagram_handle ?? '').trim()) locked.push('instagram_handle');
  if ((p.website ?? '').trim()) locked.push('website');
  return locked;
}

function gridClassFor(count: number): string {
  if (count <= 1) {
    return 'grid grid-cols-1 gap-6 max-w-sm mx-auto';
  }
  if (count === 2) {
    return 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto';
  }
  return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
}

function deriveInitialValues(
  ctx: OrganizationAiContextResponse | null,
): WbBusinessInfo {
  if (!ctx?.parsed) {
    return {
      business_name: '',
      business_type: '',
      services: '',
      support_phone: '',
      support_email: '',
      instagram_handle: '',
      website: '',
    };
  }
  const p = ctx.parsed;
  return {
    business_name: p.business_name ?? '',
    business_type: '',
    services: p.services ?? '',
    support_phone: p.support_phone ?? '',
    support_email: p.support_email ?? '',
    instagram_handle: p.instagram_handle ?? '',
    website: p.website ?? '',
  };
}

function resolveThumbnailBaseUrl(): string {
  const env = (import.meta as any).env ?? {};
  if (env.VITE_CDN_URL) {
    return `${String(env.VITE_CDN_URL).replace(/\/+$/, '')}/`;
  }
  const apiBase = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
  return `${apiBase}/wb/template-asset/`;
}

interface TemplateGalleryState {
  catalogLoading: boolean;
  catalogError: string | null;
  templates: WbTemplateCatalogEntry[];
  contextLoading: boolean;
  aiContext: OrganizationAiContextResponse | null;
  miniFormTemplateId: string | null;
  instantiateTemplateId: string | null;
  instantiateBody: WbInstantiateBody | null;
  events: WbInstantiateEvent[];
  status: 'idle' | 'streaming' | 'done' | 'error';
  errorMessage?: string;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  addToSiteId,
  onComplete,
  onClose,
  onStartBlank,
}) => {
  const auth = (() => {
    try {
      return useAuth();
    } catch {
      return null;
    }
  })();
  const isAdmin = (() => {
    const u = auth?.user;
    if (!u) return false;
    if (u.is_super_admin === true) return true;
    const role = (u.role ?? '').toString().trim().toUpperCase();
    return role === 'ADMIN';
  })();

  const [state, setState] = useState<TemplateGalleryState>({
    catalogLoading: true,
    catalogError: null,
    templates: [],
    contextLoading: true,
    aiContext: null,
    miniFormTemplateId: null,
    instantiateTemplateId: null,
    instantiateBody: null,
    events: [],
    status: 'idle',
    errorMessage: undefined,
  });

  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const thumbnailBaseUrl = useMemo(() => resolveThumbnailBaseUrl(), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({
      ...s,
      catalogLoading: true,
      contextLoading: true,
      catalogError: null,
    }));

    void Promise.allSettled([
      wbTemplatesApi.list(),
      organizationApi.getAiContext(),
    ]).then(([catalogRes, ctxRes]) => {
      if (cancelled) return;
      let templates: WbTemplateCatalogEntry[] = [];
      let catalogError: string | null = null;
      if (catalogRes.status === 'fulfilled') {
        const r: any = catalogRes.value;
        if (r?.error) {
          catalogError = r.error;
        } else {
          const payload = r?.data?.data ?? r?.data ?? r;
          if (Array.isArray(payload)) templates = payload;
          else if (Array.isArray(payload?.templates)) templates = payload.templates;
        }
      } else {
        catalogError =
          catalogRes.reason instanceof Error
            ? catalogRes.reason.message
            : 'Failed to load templates';
      }

      let aiContext: OrganizationAiContextResponse | null = null;
      if (ctxRes.status === 'fulfilled' && ctxRes.value.ok) {
        aiContext = ctxRes.value.data;
      }

      setState((s) => ({
        ...s,
        catalogLoading: false,
        contextLoading: false,
        catalogError,
        templates,
        aiContext,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.status !== 'streaming') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.status]);

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, []);

  const businessType = (state.aiContext?.parsed as any)?.business_type ?? null;

  const { recommended, others } = useMemo(() => {
    const rec: WbTemplateCatalogEntry[] = [];
    const oth: WbTemplateCatalogEntry[] = [];
    if (!businessType) {
      return { recommended: [], others: state.templates };
    }
    for (const t of state.templates) {
      if (Array.isArray(t.business_types) && t.business_types.includes(businessType)) {
        rec.push(t);
      } else {
        oth.push(t);
      }
    }
    return { recommended: rec, others: oth };
  }, [state.templates, businessType]);

  const handleCardSelect = useCallback(
    (template: WbTemplateCatalogEntry) => {
      if (isAiContextSufficient(state.aiContext)) {
        const body: WbInstantiateBody = {
          siteName: state.aiContext?.parsed?.business_name?.trim() || undefined,
          ...(addToSiteId ? { addToSiteId } : {}),
        };
        startInstantiate(template.id, body);
        return;
      }
      setState((s) => ({ ...s, miniFormTemplateId: template.id }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.aiContext, addToSiteId],
  );

  const handleMiniFormSubmit = useCallback(
    (values: WbBusinessInfo) => {
      const templateId = state.miniFormTemplateId;
      if (!templateId) return;
      const body: WbInstantiateBody = {
        siteName: values.business_name?.trim() || undefined,
        businessInfo: values,
        ...(addToSiteId ? { addToSiteId } : {}),
      };
      setState((s) => ({ ...s, miniFormTemplateId: null }));
      startInstantiate(templateId, body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.miniFormTemplateId, addToSiteId],
  );

  const startInstantiate = useCallback(
    (templateId: string, body: WbInstantiateBody) => {
      setState((s) => ({
        ...s,
        instantiateTemplateId: templateId,
        instantiateBody: body,
        events: [],
        status: 'streaming',
        errorMessage: undefined,
      }));

      void wbTemplatesApi.instantiateStream(
        templateId,
        body,
        (event) => {
          setState((s) => ({ ...s, events: [...s.events, event] }));
          if (event.type === 'done') {
            const siteId = event.site_id;
            const firstPageId = event.first_page_id;
            setState((s) => ({ ...s, status: 'done' }));
            if (completionTimerRef.current) {
              clearTimeout(completionTimerRef.current);
            }
            completionTimerRef.current = setTimeout(() => {
              completionTimerRef.current = null;
              onComplete(siteId, firstPageId);
            }, 500);
          }
        },
        () => {
          setState((s) =>
            s.status === 'streaming' ? { ...s, status: 'done' } : s,
          );
        },
        (msg) => {
          setState((s) => ({
            ...s,
            status: 'error',
            errorMessage: msg || 'Something went wrong',
          }));
        },
      );
    },
    [onComplete],
  );

  const handleRetry = useCallback(() => {
    const { instantiateTemplateId, instantiateBody } = state;
    if (!instantiateTemplateId || !instantiateBody) return;
    startInstantiate(instantiateTemplateId, instantiateBody);
  }, [state, startInstantiate]);

  const handlePickDifferent = useCallback(() => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    setState((s) => ({
      ...s,
      instantiateTemplateId: null,
      instantiateBody: null,
      events: [],
      status: 'idle',
      errorMessage: undefined,
    }));
  }, []);

  const isLoading = state.catalogLoading || state.contextLoading;
  const showOverlay = state.status === 'streaming' || state.status === 'error';
  const showDoneOverlay = state.status === 'done';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="wb-template-gallery">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {addToSiteId ? 'Add a page from a template' : 'Pick a template to get started'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Personalized with your business info — ready to edit in seconds.
          </p>
          {onStartBlank && (
            <button
              type="button"
              onClick={onStartBlank}
              disabled={state.status === 'streaming'}
              data-testid="wb-template-gallery-start-blank"
              className="mt-3 text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Or start with a blank site →
            </button>
          )}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="wb-template-gallery-close"
            aria-label="Close gallery"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isLoading && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="wb-template-gallery-skeleton"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-gray-200 bg-gray-100 h-72 animate-pulse"
              data-testid="wb-template-gallery-skeleton-card"
            />
          ))}
        </div>
      )}

      {!isLoading && state.catalogError && (
        <div
          className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-3"
          data-testid="wb-template-gallery-error"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Could not load templates</div>
            <div className="text-xs mt-1">{state.catalogError}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            data-testid="wb-template-gallery-error-retry"
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !state.catalogError && state.templates.length === 0 && (
        <div
          className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl"
          data-testid="wb-template-gallery-empty"
        >
          <h3 className="text-base font-semibold text-gray-700">
            No templates available yet
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Check back soon — we're adding new ones every week.
          </p>
        </div>
      )}

      {!isLoading && !state.catalogError && recommended.length > 0 && (
        <section className="mb-8" data-testid="wb-template-gallery-recommended-section">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Recommended for your business
          </h2>
          <div className={gridClassFor(recommended.length)}>
            {recommended.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                thumbnailBaseUrl={thumbnailBaseUrl}
                isRecommended={true}
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </section>
      )}

      {!isLoading && !state.catalogError && others.length > 0 && (
        <section data-testid="wb-template-gallery-all-section">
          {recommended.length > 0 && (
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              All templates
            </h2>
          )}
          <div className={gridClassFor(others.length)}>
            {others.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                thumbnailBaseUrl={thumbnailBaseUrl}
                isRecommended={false}
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </section>
      )}

      {state.miniFormTemplateId && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          data-testid="wb-template-gallery-mini-form-modal"
        >
          <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-5 my-8">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Tell us about your business
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  We'll use this to personalize your template.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setState((s) => ({ ...s, miniFormTemplateId: null }))}
                aria-label="Close form"
                data-testid="wb-template-gallery-mini-form-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <MiniForm
              initialValues={deriveInitialValues(state.aiContext)}
              lockedFields={deriveLockedFields(state.aiContext)}
              isAdmin={isAdmin}
              onSubmit={handleMiniFormSubmit}
            />
          </div>
        </div>
      )}

      {(showOverlay || showDoneOverlay) && (
        <InstantiateProgress
          events={state.events}
          status={state.status === 'idle' ? 'streaming' : state.status}
          errorMessage={state.errorMessage}
          onRetry={handleRetry}
          onPickDifferent={handlePickDifferent}
        />
      )}
    </div>
  );
};

export default TemplateGallery;
